/**
 * Central landing-page copy and light “dynamic” helpers (env + rotation).
 * Plain language first — avoid jargon like “radius”, “counter”, “bidding”.
 */

export const BRAND_NAME = "HealBid";

/** Layman phrase for map distance filter (reused across sections). */
export const SEARCH_AREA_LAYMAN =
  "how far you're willing to travel from your home or chosen area to do the operation ";

/** Core value proposition */
export const PLATFORM_VALUE_PROP =
  "Get surgery prices that work for you—from trusted doctors and hospitals, without giving up quality. Pick your surgery, upload prescriptions, reports, or insurance papers, say how much you can spend and " +
  SEARCH_AREA_LAYMAN +
  ". Doctors and hospitals send their prices. You can ask for a better price if needed. When you're happy with an offer, choose where to go and HealBid helps fix your appointment.";

/** How HealBid Works — paragraph 1 */
export const WORKFLOW_SUBMIT =
  "You dictate what you want to pay. Tell us your surgery, how much you are willing to spend, and " +
  SEARCH_AREA_LAYMAN +
  ". Doctors and hospitals then reply with prices based on the cost you set.";

/** @deprecated Use WORKFLOW_SUBMIT — kept for FAQ imports */
export const WORKFLOW_BRIDGE = WORKFLOW_SUBMIT;

/** How HealBid Works — paragraph 2 */
export const WORKFLOW_SHARE_REPORTS =
  "Upload your prescriptions, reports, or insurance papers once. Your price stays in your hands: doctors and hospitals see the amount you chose and send clear written quotes—without you visiting each clinic and paying fees just to ask how much surgery costs.";

/** How HealBid Works — paragraph 3 */
export const WORKFLOW_COMPARE_AND_BID =
  "Compare each quote  Ask for a better price if an offer is above what you set. Then pick the doctor or hospital you trust—HealBid helps confirm your appointment.";

/** How HealBid Works — section subheading */
export const WORKFLOW_SECTION_SUBHEAD =
  "You set the surgery cost you want to pay—doctors and hospitals respond with their offers.";

/** Hero / mid-page CTA body (Respond section) */
export const HERO_BUDGET_BODY =
  "Pick your surgery and upload prescriptions, reports, or insurance papers. Say how much you can spend and " +
  SEARCH_AREA_LAYMAN +
  ". Doctors and hospitals reply with prices—you can ask for a better price until you're ready. Then choose your hospital or clinic and HealBid helps book your appointment.";

/** Banner subcopy */
export const BANNER_BODY =
  "Stop visiting many clinics and paying fees just to learn the price. On HealBid, say how much you want to spend for your operation and " +
  SEARCH_AREA_LAYMAN +
  ". Trusted doctors and hospitals send prices you can compare—and you can ask for a better price before you book.";

/** “Why patients choose” section tagline */
export const WHY_PATIENTS_TAGLINE =
  "Tell us your budget. Get real prices. Compare, ask for a better price, and book—with trusted care.";

/**
 * Optional audited counter from build/runtime env (Create React App: REACT_APP_*).
 * If unset, components should use non-numeric trust language instead of inventing stats.
 */
export function getOptionalBookingsDisplay() {
  const raw = process.env.REACT_APP_LANDING_BOOKINGS_COUNT;
  if (raw == null || String(raw).trim() === "") return null;
  const n = parseInt(String(raw).replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

export function getBannerTrustBlock() {
  const n = getOptionalBookingsDisplay();
  if (n != null) {
    return {
      headline: `${n.toLocaleString()}+ surgery price requests`,
      subline: "Patients comparing surgery costs before they book.",
    };
  }
  return {
    headline: "Free surgery price comparison",
    subline: "Set your budget and travel distance—compare quotes before you book.",
  };
}

/** Four-step offer cards on the landing page */
export const HOSPITAL_OFFER_STEPS = [
  {
    title: "Choose surgery & upload",
    desc: "Pick your procedure and share prescriptions, reports, or insurance papers once.",
  },
  {
    title: "Set budget & travel distance",
    desc:
      "Say how much you can spend and how far from your location you're okay going—for example, only nearby hospitals or anywhere in your city.",
  },
  {
    title: "Compare prices",
    desc: "Read each quote side by side. Ask for a better price if something doesn't fit.",
  },
  {
    title: "Pick & book",
    desc: "Choose the doctor or hospital you like. HealBid helps you confirm your appointment.",
  },
];

/** Rotating illustrative provider labels — not live roster. */
export function getExampleProviderPair() {
  const pairs = [
    {
      a: { name: "Orthopedic surgeon", role: "Reviews your knee surgery plan, implant options, and surgeon fee." },
      b: { name: "Hospital coordinator", role: "Shares theatre charges, room type, and admission date." },
    },
    {
      a: { name: "Gynecology specialist", role: "Explains the procedure and expected recovery time." },
      b: { name: "Hospital billing desk", role: "Lists what is included in the bill and the full surgery cost." },
    },
    {
      a: { name: "ENT surgeon", role: "Shares the surgery plan and doctor charges." },
      b: { name: "Diagnostics team", role: "Lists tests and reports needed before surgery." },
    },
  ];
  const i = Math.floor(Date.now() / 86400000) % pairs.length;
  return pairs[i];
}

/** Animated “why choose” cards */
export const WHY_CHOOSE_CARDS = [
  {
    small: "Step 1",
    title: "Tell us what you need",
    desc:
      "Pick your surgery, say how much you can spend, and choose " + SEARCH_AREA_LAYMAN + ". Quotes come from doctors and hospitals in that area.",
  },
  {
    small: "Step 2",
    title: "Compare prices in one place",
    desc: "Read each quote side by side. Ask for a better price if you want—without paying consultation fees at many clinics just to ask cost.",
  },
  {
    small: "Step 3",
    title: "Book when you're ready",
    desc: "Choose the hospital or clinic you like. HealBid helps you confirm your appointment.",
  },
];

/** “Why choose HealBid” bullet copy (Healthcare section) */
export const WHY_CHOOSE_BULLETS = {
  safe:
    "Share only the prescriptions, reports, or insurance papers you choose. Your request goes securely to matched doctors and hospitals—you don't repeat your story at every clinic.",
  clearPricing:
    "Say your budget and " + SEARCH_AREA_LAYMAN + ". You get written quotes from the doctors and hospitals against your bidding amount.",
  trusted:
    "Compare verified doctors and hospitals and pick the option that fits your treatment and budget—without giving up quality.",
  yourChoice:
    "Ask for a better price if you need to, then choose where to go. HealBid helps confirm your appointment.",
};

/** FAQ defaults */
export const LANDING_FAQ_DEFAULTS = [
  {
    question: "How does getting a surgery price work?",
    answer: WORKFLOW_SUBMIT,
  },
  {
    question: "Is my information safe?",
    answer:
      "Yes. We protect your data in transit and at rest, as explained in our consent forms. We do not sell your health information for ads. Only the doctors and hospitals you match with see the files you choose to share.",
  },
  {
    question: "Can a doctor or hospital offer a different price than I asked?",
    answer:
      "Yes. You say how much you can spend. They can accept that amount or suggest a different price. You can suggest another price too, until you both agree.",
  },
  {
    question: "What if no one replies?",
    answer:
      "Try raising your budget a little, searching a wider area (more travel distance), or wait for new quotes. Our support team can also help you improve your request.",
  },
  {
    question: "How do I book after I pick a quote?",
    answer:
      "Choose the quote you like. HealBid connects you with that clinic to fix your appointment. You'll see the details in the app and by email.",
  },
  {
    question: "Who sends prices — hospitals or doctors?",
    answer:
      "Both can. Doctors and hospitals near the area you picked can reply with a full price quote. You compare everything in one app before you decide where to book.",
  },
  {
    question: "What does “how far you'll travel” mean?",
    answer:
      "You choose how close or far care can be from your home or pin on the map—for example, only within 5 km, your whole city, or a bit beyond. Only doctors and hospitals in that range see your request.",
  },
];

export const LANDING_FAQ_SUBTITLE =
  "Simple answers about surgery prices, travel distance, privacy, and booking.";

/** Contact / get-started hero (non-landing pages) */
export const CONTACT_HERO_BODY =
  "Compare surgery prices from hospitals and doctors, say how much you can spend and " +
  SEARCH_AREA_LAYMAN +
  ", and book only when the price and timing work for you.";
